import AllowedResizeTypes from '../store/schema/AllowedResizeTypes';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import canResizeItem from './canResizeItem';
import { addDays, getTimestamp, endOfDay, OwaDate } from 'owa-datetime';
import { isEventLongerThanTwentyFourHours } from 'owa-calendar-events-visual-date-range';
import { DateRange, TimeConstants, getDateRangeLength } from 'owa-datetime-utils';
import { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import getDisplayMonthRangeStart from './getDisplayMonthRangeStart';
import { SurfaceDragDropConfig, CalendarSurfaceStore } from 'owa-calendar-surface-dategrid';

/**
 * Get the allowed resize types for an item in the regular day/week time grid
 * @param store The surface store
 * @param dragDropConfig The drag and drop config
 * @param item The item
 * @param columnIndex The column index for the item
 * @return The allowed resize types for the item
 */
export function getAllowedResizeTypesForRegularItem(
    store: CalendarSurfaceStore,
    dragDropConfig: SurfaceDragDropConfig,
    item: CalendarEvent,
    getColumnDate: (index: number) => OwaDate,
    columnIndex: number
): AllowedResizeTypes {
    if (canResizeItem(store, dragDropConfig, item)) {
        // For regular grid items, the container date range is the day in which the item is being rendered,
        // this way we can properly handle multi-day < 24h events resize handles
        let columnStart = getColumnDate(columnIndex);
        let columnEnd = endOfDay(columnStart);
        let containerDateRange = { start: columnStart, end: columnEnd };
        return getAllowedResizeTypes(item, containerDateRange, store.dateRangeType);
    } else {
        return {
            resizeTop: false,
            resizeLeft: false,
            resizeBottom: false,
            resizeRight: false,
        };
    }
}

/**
 * Gets the allowed resize types for an item in the all-day well
 * @param store The surface store
 * @param dragDropConfig The drag and drop config
 * @param item The item
 * @return The allowed resize types for the item
 */
export function getAllowedResizeTypesForAllDayItem(
    store: CalendarSurfaceStore,
    dragDropConfig: SurfaceDragDropConfig,
    item: CalendarEvent
): AllowedResizeTypes {
    if (canResizeItem(store, dragDropConfig, item)) {
        // For all day events, the date range containing the all day events is the store date range itself, adding
        // one day to account for the end of the last day
        let containerDateRange = {
            start: store.dateRange.start,
            end: addDays(store.dateRange.end, 1),
        };
        return getAllowedResizeTypes(item, containerDateRange, store.dateRangeType);
    } else {
        return {
            resizeTop: false,
            resizeLeft: false,
            resizeBottom: false,
            resizeRight: false,
        };
    }
}

/**
 * Gets the allowed resize types for an item in the month view
 * @param store The surface store
 * @param dragDropConfig The drag and drop config
 * @param item The item
 * @param weekIndex The week index for the item
 * @return The allowed resize types for the item
 */
export function getAllowedResizeTypesForMonthItem(
    store: CalendarSurfaceStore,
    dragDropConfig: SurfaceDragDropConfig,
    item: CalendarEvent,
    weekIndex: number
) {
    if (canResizeItem(store, dragDropConfig, item)) {
        let displayRangeStart: OwaDate = getDisplayMonthRangeStart(store);
        let containingWeekStart: OwaDate = addDays(
            displayRangeStart,
            weekIndex * TimeConstants.DaysInOneWeek
        );
        let containingWeekEnd: OwaDate = addDays(containingWeekStart, TimeConstants.DaysInOneWeek);
        let containingWeekRange: DateRange = { start: containingWeekStart, end: containingWeekEnd };
        return getAllowedResizeTypes(item, containingWeekRange, store.dateRangeType);
    } else {
        return {
            resizeTop: false,
            resizeLeft: false,
            resizeBottom: false,
            resizeRight: false,
        };
    }
}

function getAllowedResizeTypes(
    item: CalendarEvent,
    containerRange: DateRange,
    dateRangeType: DateRangeType
): AllowedResizeTypes {
    let isMultiDayItem = isEventLongerThanTwentyFourHours(item);
    if (
        dateRangeType == DateRangeType.Day &&
        (item.IsAllDayEvent || isMultiDayItem) &&
        getDateRangeLength(containerRange) === 1
    ) {
        return {
            resizeTop: false,
            resizeBottom: false,
            resizeLeft: false,
            resizeRight: false,
        };
    }

    let itemStartsAfterRange = getTimestamp(item.Start) >= getTimestamp(containerRange.start);
    let itemEndsBeforeRange = getTimestamp(item.End) <= getTimestamp(containerRange.end);
    let resizeLeft = isMultiDayItem && itemStartsAfterRange;
    let resizeRight = isMultiDayItem && itemEndsBeforeRange;

    if (dateRangeType == DateRangeType.Month) {
        return {
            resizeTop: false,
            resizeBottom: false,
            resizeLeft,
            resizeRight,
        };
    } else {
        return {
            resizeTop: !isMultiDayItem && itemStartsAfterRange,
            resizeBottom: !isMultiDayItem && itemEndsBeforeRange,
            resizeLeft,
            resizeRight,
        };
    }
}
